import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Minus } from "lucide-react";
import PageWrap from "@/fileshare/components/PageWrap";
import { useAuth } from "@/fileshare/context/AuthContext";
import api from "@/fileshare/lib/api";
import { formatApiErrorDetail } from "@/fileshare/lib/format";
import { Button } from "@/fileshare/components/ui/button";

const PLANS = [
  {
    id: "free", name: "Free", price: "$0", sub: "No signup needed",
    highlight: false,
    features: ["2 GB max file size", "1 day max expiry", "50 downloads per link", "Password protection", "Random links"],
  },
  {
    id: "plus", name: "Plus", price: "$9", sub: "per month",
    highlight: true,
    features: ["10 GB max file size", "100 GB total storage", "Up to 90 days expiry", "Unlimited downloads", "Custom link slugs", "Dashboard & file management"],
  },
  {
    id: "business", name: "Business", price: "$49", sub: "per month",
    highlight: false,
    features: ["50 GB max file size", "1,000 GB total storage", "No expiry option", "Unlimited downloads", "Custom link slugs", "File request links", "Dashboard & file management"],
  },
];

const MATRIX = [
  ["Max file size", "2 GB", "10 GB", "50 GB"],
  ["Total storage", "—", "100 GB", "1,000 GB"],
  ["Max expiry", "1 day", "90 days", "Unlimited"],
  ["Downloads per link", "50", "Unlimited", "Unlimited"],
  ["Custom link slug", false, true, true],
  ["Password protection", true, true, true],
  ["File request links", false, false, true],
  ["Dashboard", false, true, true],
];

function Cell({ v }) {
  if (v === true) return <Check className="h-4 w-4 text-blue-500 mx-auto" />;
  if (v === false) return <Minus className="h-4 w-4 text-slate-300 mx-auto" />;
  return <span className="text-sm text-slate-600">{v}</span>;
}

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loadingId, setLoadingId] = useState(null);

  const choose = async (planId) => {
    if (planId === "free") { navigate("/fileshares"); return; }
    if (!user) { navigate("/fileshares/signup"); return; }
    if (user.tier === planId) { toast.info("You're already on this plan."); return; }
    setLoadingId(planId);
    try {
      const { data } = await api.post("/payments/create-checkout-session", {
        package_id: planId,
        origin_url: window.location.origin + "/fileshares",
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout");
      setLoadingId(null);
    }
  };

  return (
    <PageWrap className="max-w-6xl mx-auto px-6 pt-16 pb-24">
      <div className="max-w-2xl">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900">Simple, honest pricing</h1>
        <p className="mt-4 text-base text-slate-600">Start free. Upgrade when you need bigger files, longer retention, and management tools.</p>
      </div>

      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {PLANS.map((p, i) => (
          <motion.div
            key={p.id}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06, duration: 0.4 }}
            className={`rounded-lg p-7 bg-white flex flex-col ${
              p.highlight ? "border-2 border-blue-500 shadow-[0_8px_30px_rgba(59,130,246,0.12)]" : "border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
            }`}
            data-testid={`plan-card-${p.id}`}
          >
            {p.highlight && (
              <span className="self-start mb-3 text-xs font-semibold uppercase tracking-wide text-blue-500 bg-blue-50 px-2.5 py-1 rounded-full">Most popular</span>
            )}
            <h3 className="text-lg font-semibold text-slate-900">{p.name}</h3>
            <div className="mt-2 flex items-end gap-1.5">
              <span className="text-4xl font-bold tracking-tight text-slate-900">{p.price}</span>
              <span className="text-sm text-slate-500 mb-1.5">{p.sub}</span>
            </div>
            <ul className="mt-6 space-y-2.5 flex-1">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Button
              data-testid={`plan-cta-${p.id}`}
              onClick={() => choose(p.id)}
              disabled={loadingId === p.id || (user && user.tier === p.id)}
              className={`mt-7 w-full h-11 ${p.highlight ? "bg-blue-500 hover:bg-blue-600 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}`}
            >
              {user && user.tier === p.id ? "Current plan" : loadingId === p.id ? "Redirecting…" : p.id === "free" ? "Get started" : `Upgrade to ${p.name}`}
            </Button>
          </motion.div>
        ))}
      </div>

      {/* comparison table */}
      <div className="mt-16 overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left py-4 text-sm font-semibold text-slate-900">Features</th>
              <th className="py-4 text-sm font-semibold text-slate-900 text-center">Free</th>
              <th className="py-4 text-sm font-semibold text-blue-500 text-center">Plus</th>
              <th className="py-4 text-sm font-semibold text-slate-900 text-center">Business</th>
            </tr>
          </thead>
          <tbody>
            {MATRIX.map((row) => (
              <tr key={row[0]} className="border-b border-slate-100">
                <td className="py-3.5 text-sm text-slate-700">{row[0]}</td>
                <td className="py-3.5 text-center"><Cell v={row[1]} /></td>
                <td className="py-3.5 text-center"><Cell v={row[2]} /></td>
                <td className="py-3.5 text-center"><Cell v={row[3]} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PageWrap>
  );
}
